import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Sparkles, Loader2, Minimize2, ArrowRight, LayoutGrid } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import ConciergeConversation from "@/components/concierge/ConciergeConversation";
import ConciergeListingsPanel from "@/components/concierge/ConciergeListingsPanel";
import ConciergeMatchesSheet from "@/components/concierge/ConciergeMatchesSheet";
import ConciergeThreadMenu from "@/components/concierge/ConciergeThreadMenu";
import { useConciergeConversation } from "@/components/concierge/useConciergeConversation";
import { useIsMobile } from "@/hooks/use-mobile";
import { useAuth } from "@/hooks/useAuth";
import { useSiteAsset } from "@/hooks/useSiteAsset";
import { setIntent } from "@/lib/onboardingIntent";
import { track } from "@/lib/track";
import { useChatViewport } from "@/hooks/useChatViewport";
import HeroVideo from "@/components/HeroVideo";

// Minimalist renter concierge. On the home page it stays hidden behind a small
// "AI concierge" pill and only reveals the module when tapped; from there it
// auto-expands to a full-screen takeover on mobile (and a chat-left /
// listings-right split on desktop). Renders nothing when the tenant has the
// concierge switched off (ai_tone_config.enabled). The conversation itself is
// the shared <ConciergeConversation />, this file is only the entry chrome.

const ConciergeChat = () => {
  const [expanded, setExpanded] = useState(false);
  // Home-page gate: the whole module is hidden until the visitor taps the
  // "AI concierge" pill, so it doesn't dominate the page.
  const [revealed, setRevealed] = useState(false);
  const [matchesOpen, setMatchesOpen] = useState(false);
  const conv = useConciergeConversation({
    surface: "home",
    onBeforeSend: () => setExpanded(true), // go full-screen the moment they send, no manual expand
    active: revealed, // the collapsed pill does not poll the transcript
  });
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  // The full-screen chat on phones fits what you can see with the keyboard up,
  // and the page goes back to the top when it closes (useChatViewport).
  useChatViewport(expanded && isMobile);
  const { user } = useAuth();
  // Same hero media the home page uses, so the concierge entry feels native.
  const heroAsset = useSiteAsset("home.hero", { width: 1920 });
  const heroVideoAsset = useSiteAsset("home.heroVideo");
  const useHeroVideo = !heroVideoAsset.isFallback && heroVideoAsset.mediaType === "video";

  if (conv.configLoading || !conv.enabled) return null;

  // The hero's own box sits on a photo, so it keeps its look; it behaves like
  // the chat kit's composer (src/components/chat/ChatComposer.tsx): Enter
  // sends, Shift+Enter adds a line, never mid input-method composing, and the
  // field stays usable while a message is on its way.
  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      if (!conv.sending) void conv.submit();
    }
  };

  // Same CTAs as the home hero.
  const handleFindHome = () => setIntent("renter");
  const handleListHome = () => {
    setIntent("owner");
    navigate(user ? "/dashboard" : "/owner-onboarding");
  };

  // Collapsed: show only a small pill. The full module + chat mount on tap.
  if (!revealed) {
    return (
      <section className="flex justify-center px-4 py-10 sm:py-12">
        <button
          type="button"
          onClick={() => {
            track("concierge_opened", { surface: "home" });
            setRevealed(true);
          }}
          className="group inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/5 px-5 py-2.5 text-sm font-medium text-primary shadow-sm transition-all hover:bg-primary/10 hover:shadow-md"
          aria-label="Open the AI concierge"
        >
          <Sparkles className="h-4 w-4" />
          Ask {conv.name}, our AI concierge
          <span className="hidden text-[11px] font-normal text-primary/70 sm:inline">
            · chat to explore homes
          </span>
        </button>
      </section>
    );
  }

  return (
    <>
      {/* Sleek hero-style entry. Always mounted (the overlay sits on top when
          expanded) so the page never reflows, which previously yanked scroll. */}
      <section className="relative overflow-hidden bg-charcoal">
        {useHeroVideo ? (
          <HeroVideo src={heroVideoAsset.src} className="absolute inset-0 h-full w-full object-cover" />
        ) : !heroAsset.isFallback && heroAsset.src ? (
          <img
            src={heroAsset.src}
            alt=""
            aria-hidden="true"
            loading="lazy"
            className="absolute inset-0 h-full w-full object-cover"
          />
        ) : null}
        <div className="absolute inset-0 bg-charcoal/55" />

        <div className="relative z-10 container mx-auto px-4 py-20 sm:py-28 max-w-2xl text-center">
          <div className="mb-6 inline-flex items-center gap-1.5 rounded-full border border-white/25 bg-white/10 px-3 py-1 text-[11px] font-medium text-white/90 backdrop-blur">
            <Sparkles className="h-3.5 w-3.5" /> {conv.name} · AI concierge · Beta
          </div>
          <h2
            className="mb-8 font-serif italic leading-[1.1] text-white drop-shadow"
            style={{ fontSize: "clamp(2.25rem, 6vw, 4rem)" }}
          >
            Explore homes by chatting
          </h2>

          <div className="rounded-[28px] border border-white/20 bg-white/10 p-3 text-left shadow-elevated backdrop-blur-xl sm:p-3.5">
            <Textarea
              value={conv.draft}
              onChange={(e) => conv.setDraft(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder={conv.maintenanceDisabled ? "Paused for maintenance" : "Tell me what you're looking for: area, budget, beds, dates…"}
              rows={2}
              maxLength={2000}
              disabled={conv.maintenanceDisabled}
              aria-label="Message to the concierge"
              autoComplete="off"
              autoCorrect="on"
              autoCapitalize="sentences"
              enterKeyHint="send"
              className="max-h-40 min-h-[3.25rem] resize-none border-0 bg-transparent text-[15px] text-white placeholder:text-white/60 focus-visible:ring-0 focus-visible:ring-offset-0"
            />
            <div className="mt-1 flex items-center justify-end">
              <Button
                onClick={() => void conv.submit()}
                disabled={conv.sending || conv.maintenanceDisabled || !conv.draft.trim()}
                className="gap-1.5 rounded-full px-6"
                aria-label="Ask the concierge"
              >
                {conv.sending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <>
                    Ask <ArrowRight className="h-4 w-4" />
                  </>
                )}
              </Button>
            </div>
          </div>

          <div className="mx-auto mt-6 flex w-full max-w-md flex-col items-stretch justify-center gap-3 sm:max-w-none sm:flex-row sm:items-center">
            <Link to="/properties" onClick={handleFindHome} className="sm:w-auto">
              <Button
                size="lg"
                variant="outline"
                className="w-full rounded-full border-white bg-transparent px-7 py-6 text-[13px] font-medium tracking-wide text-white hover:bg-white/10 hover:text-white sm:w-auto"
              >
                FIND YOUR HOME
              </Button>
            </Link>
            <Button
              size="lg"
              variant="outline"
              onClick={handleListHome}
              className="w-full rounded-full border-white bg-transparent px-7 py-6 text-[13px] font-medium tracking-wide text-white hover:bg-white/10 hover:text-white sm:w-auto"
            >
              LIST YOUR HOME
            </Button>
          </div>
        </div>
      </section>

      <Dialog open={expanded} onOpenChange={setExpanded}>
        <DialogContent
          keyboardPin={false}
          phoneSheet={false}
          overlayClassName="z-[100]"
          // Keep the keyboard focus in the composer (the field carries autoFocus)
          // instead of Radix's default jump to the first header button.
          onOpenAutoFocus={(e) => e.preventDefault()}
          // The shadow paints the chat's background below it, so the strip
          // between the text box and the iPhone keyboard (the autofill bar)
          // never shows the home page behind (Felipe, Oct 18).
          className="z-[100] left-0 top-[var(--chat-vv-top,0px)] translate-x-0 translate-y-0 w-full h-[var(--chat-visible-height,100dvh)] max-w-none max-h-none overflow-hidden rounded-none sm:rounded-none border-0 p-0 gap-0 flex flex-col shadow-[0_100vh_0_0_hsl(var(--background))] [&>button]:hidden"
        >
          <DialogTitle className="sr-only">AI concierge</DialogTitle>
          <header className="shrink-0 flex items-center justify-between gap-2 border-b px-4 py-2 pt-[max(0.5rem,env(safe-area-inset-top))] glass-surface">
            <span className="inline-flex items-center gap-1.5 text-sm font-semibold">
              <Sparkles className="h-4 w-4 text-primary" /> Concierge
            </span>
            <div className="flex items-center gap-1">
              {/* Desktop has the matches panel always open; phones get it on demand. */}
              {isMobile && conv.shownListings.length > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  className="rounded-full h-8 gap-1.5"
                  onClick={() => setMatchesOpen(true)}
                >
                  <LayoutGrid className="h-3.5 w-3.5" /> Matches {conv.shownListings.length}
                </Button>
              )}
              <ConciergeThreadMenu
                onNewChat={conv.reset}
                onEscalate={conv.escalate}
                continueTo={user ? "/messages?c=concierge" : undefined}
                elevated
              />
              <Button
                variant="ghost"
                size="icon"
                className="rounded-full h-9 w-9 text-muted-foreground"
                onClick={() => setExpanded(false)}
                aria-label="Minimize"
              >
                <Minimize2 className="h-4 w-4" />
              </Button>
            </div>
          </header>

          {isMobile ? (
            <ConciergeConversation conv={conv} variant="overlay-mobile" className="flex-1" autoFocus />
          ) : (
            <div className="flex-1 min-h-0 grid grid-cols-[minmax(320px,380px)_minmax(0,1fr)]">
              <ConciergeConversation conv={conv} variant="overlay-desktop" className="border-r" autoFocus />
              <ConciergeListingsPanel cards={conv.shownListings} onOpenDetail={conv.openDetail} />
            </div>
          )}
        </DialogContent>
      </Dialog>

      <ConciergeMatchesSheet
        open={matchesOpen}
        onOpenChange={setMatchesOpen}
        cards={conv.shownListings}
        onOpenDetail={conv.openDetail}
        elevated
      />
    </>
  );
};

export default ConciergeChat;
